import { getVestingTermsSimplified } from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useQuery } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { NewJoinersAndAllocationsInvestorLocal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewJoinersAndAllocationsModal/useNewJoinersAndAllocationsModalController.ts';

export const useJoinerAllocationsVesting = (
	investorsList: NewJoinersAndAllocationsInvestorLocal[],
	setInvestorsList: Dispatch<SetStateAction<NewJoinersAndAllocationsInvestorLocal[]>>
) => {
	const [vestingBulkUpdateMode, setVestingBulkUpdateMode] = useState(false);
	const [bulkVestingTerm, setBulkVestingTerm] = useState<string | null>(null);
	const [selectedInvestors, setSelectedInvestors] = useState<string[]>([]);
	const [investorVestingTerms, setInvestorVestingTerms] = useState<Map<string, string>>(
		new Map()
	);

	const getVestingTermsRequest = useQuery({
		queryKey: [API_PATHS.MIP_VESTING_TERM_NAMES_GET],
		queryFn: ({ signal }) =>
			getVestingTermsSimplified(API_PATHS.MIP_VESTING_TERM_NAMES_GET, signal),
	});

	const vestingTerms = getVestingTermsRequest.data?.payload;

	useEffect(() => {
		if (investorsList?.length && !!vestingTerms?.length) {
			const firstTerm = vestingTerms?.[0]?.id || '';
			const newMap = new Map();

			investorsList.forEach((investor) =>
				newMap.set(investor.email, investor.vestingTermId || firstTerm)
			);
			setInvestorVestingTerms(newMap);
		}
	}, [investorsList]);

	useEffect(() => {
		const mappedInvestors = investorsList.map((investor) => {
			const vestingTermId =
				investorVestingTerms.get(investor.email) || vestingTerms?.[0]?.id || '';
			return { ...investor, vestingTermId };
		});

		setInvestorsList((prevList) => {
			const hasChanged = mappedInvestors.some((updated, index) => {
				return updated.vestingTermId !== prevList[index]?.vestingTermId;
			});
			return hasChanged ? mappedInvestors : prevList;
		});
	}, [investorVestingTerms]);

	const onInvestorCheckboxClick = (email: string) => {
		const isSelected = selectedInvestors.includes(email);
		const isDifferentValueSetToMap = investorVestingTerms.get(email) !== bulkVestingTerm;

		setSelectedInvestors((prev) => {
			if (isSelected) {
				return prev.filter((item) => item !== email);
			} else {
				return [...prev, email];
			}
		});

		if (isSelected && bulkVestingTerm && isDifferentValueSetToMap) {
			setInvestorVestingTerms((prev) => {
				const newMap = new Map(prev);
				newMap.set(email, bulkVestingTerm || '');
				return newMap;
			});
		}
	};

	const onInvestorDropdownChange = (email: string, value = '') => {
		setInvestorVestingTerms((prev) => new Map(prev).set(email, value));
	};

	const onBulkVestingTermChange = (value: string) => {
		if (selectedInvestors.length) {
			setInvestorVestingTerms((prev) => {
				const newMap = new Map(prev);
				selectedInvestors.forEach((email) => {
					if (newMap.get(email) !== value) {
						newMap.set(email, value);
					}
				});
				return newMap;
			});
		}
		setBulkVestingTerm(value);
		setVestingBulkUpdateMode(false);
	};

	const onBulkUpdateModeChange = (isBulkUpload: boolean) => {
		setVestingBulkUpdateMode(isBulkUpload);
	};

	const isAllSelected = () => investorsList.every((inv) => selectedInvestors.includes(inv.email));

	const selectAllInvestors = () => {
		const allSelected = isAllSelected();

		if (allSelected) {
			setSelectedInvestors([]);
		} else {
			const allEmails = investorsList.map((investor) => investor.email);
			setSelectedInvestors(allEmails);
		}
	};

	return {
		vestingTerms,
		investorVestingTerms,
		onInvestorCheckboxClick,
		onInvestorDropdownChange,
		onBulkVestingTermChange,
		vestingBulkUpdateMode,
		onBulkUpdateModeChange,
		bulkVestingTerm,
		setBulkVestingTerm,
		selectedInvestors,
		selectAllInvestors,
		isAllInvestorsSelected: isAllSelected,
	};
};
